import { fetchBot } from '@/services/services';
import { MARKET_LOCATION_EXTENSION } from '@bariendo/lib/extensions';
import { coverageSourceCode } from '@bariendo/lib/insurance-prefill';
import { answerString, PatientAccessCallAnswers } from '@bariendo/lib/patient-access-note';
import {
  insuranceTravelMarket,
  PN_INSURANCE_BOOK_ONCE_APPROVED_ID,
  PN_INSURANCE_ELIGIBILITY_ID,
  PN_INSURANCE_SOURCE_ID,
  PN_INSURANCE_TRAVEL_ID,
  PN_INSURER_ID,
  PN_INSURER_NOT_LISTED,
  PN_POLICYHOLDER_ID,
} from '@bariendo/lib/pn-consultation-script';
import { createReference, MedplumClient } from '@medplum/core';
import { Coverage, Organization, Patient, Reference } from '@medplum/fhirtypes';
import type { SetPatientIntakeSelectionInput } from '../../../../../../bots/src/handlers/set-patient-intake-selection';
import type { UpdateInsuranceCoverageInput } from '../../../../../../bots/src/handlers/update-insurance-coverage';

/**
 * What the insurance section's next step writes besides the track and the
 * task (BAR-2922). Each reads the answers that apply, never the raw draft.
 */

/**
 * What the Care Coordinator needs to verify the plan and start the prior
 * authorization. Not the member ID or group number: Tasks are read more widely
 * than the Coverage (Marketing reads Orbit Tasks, never Coverage), and the
 * Care Coordinator reads them from the Coverage this step writes.
 */
export function insuranceTaskDescription(answers: PatientAccessCallAnswers): string {
  const row = (label: string, id: string) => {
    const value = answerString(answers.values[id]);
    return value ? `${label}: ${value}` : undefined;
  };
  return [
    row('Insurer', PN_INSURER_ID),
    row('How they get insurance', PN_INSURANCE_SOURCE_ID),
    row('Whose plan', PN_POLICYHOLDER_ID),
    row('Likely eligibility', PN_INSURANCE_ELIGIBILITY_ID),
    row('Insurance location', PN_INSURANCE_TRAVEL_ID),
    row('Book once approved', PN_INSURANCE_BOOK_ONCE_APPROVED_ID),
  ]
    .filter(Boolean)
    .join('\n');
}

/**
 * The member ID and group number the card's insurance card rows hold. Never
 * answers: the note is read by roles that cannot read Coverage, so these go
 * only to the Coverage.
 */
export interface InsuranceCardDetails {
  memberId?: string;
  groupNumber?: string;
}

/**
 * The card's insurance answers as a partial `update-insurance-coverage` input:
 * the insurer and source the call recorded, plus any member ID and group
 * number the card rows hold for the track switch, so an unanswered row never
 * clears what signup wrote. A chosen insurer goes with them, so a member ID
 * taken for a different insurer doesn't land under the payor signup recorded.
 * Undefined when there is nothing to write.
 */
export function insuranceCoverageInput(
  patient: Patient,
  answers: PatientAccessCallAnswers,
  card: InsuranceCardDetails = {}
): UpdateInsuranceCoverageInput | undefined {
  const insurer = answerString(answers.values[`${PN_INSURER_ID}Reference`]);
  const memberId = card.memberId?.trim();
  const groupNumber = card.groupNumber?.trim();
  const coverageSource = coverageSourceCode(answerString(answers.values[PN_INSURANCE_SOURCE_ID]));
  const fields: Omit<UpdateInsuranceCoverageInput, 'patient'> = {
    // The stored reference from the insurer select, never built from an id.
    ...(insurer?.startsWith('Organization/') ? { insurer: { reference: insurer } as Reference<Organization> } : {}),
    // "Mine isn't listed" on the v2.0 card (BAR-2998): the bot's null, the patient as payor.
    ...(answerString(answers.values[PN_INSURER_ID]) === PN_INSURER_NOT_LISTED ? { insurer: null } : {}),
    ...(memberId ? { memberId } : {}),
    ...(groupNumber ? { groupNumber } : {}),
    ...(coverageSource ? { coverageSource } : {}),
  };
  if (Object.keys(fields).length === 0) return undefined;
  return { patient: createReference(patient), ...fields };
}

/** A transaction refused because the Coverage changed under it. */
function isPreconditionFailed(error: unknown): boolean {
  return error instanceof Error && /precondition failed|\b412\b/i.test(error.message);
}

/**
 * One `update-insurance-coverage` call, returning the Coverage it saved. A 412
 * means the Coverage changed between the bot's read and its write (another
 * row's save, or bariendo-be adopting it for a new episode): the bot re-reads
 * on every call, so one retry is the re-read.
 */
export async function updateInsuranceCoverage(
  medplum: MedplumClient,
  input: UpdateInsuranceCoverageInput
): Promise<Coverage> {
  try {
    return await fetchBot<Coverage>(medplum, 'update-insurance-coverage', input);
  } catch (error) {
    if (!isPreconditionFailed(error)) throw error;
    return fetchBot<Coverage>(medplum, 'update-insurance-coverage', input);
  }
}

/**
 * Writes the card's insurance answers, and the card details its rows held, to
 * the patient's insurance Coverage. The bot only edits a Coverage, so this
 * runs after the switch to the Insurance track, which creates or adopts one.
 */
export async function writeInsuranceCoverage(
  medplum: MedplumClient,
  patient: Patient,
  answers: PatientAccessCallAnswers,
  card: InsuranceCardDetails = {}
): Promise<void> {
  const input = insuranceCoverageInput(patient, answers, card);
  if (!input) return;
  await updateInsuranceCoverage(medplum, input);
}

/**
 * Sets the Intake and billing Location to the insurance travel location, where
 * the procedure will happen. Nothing for "Not willing to travel".
 */
export async function writeInsuranceTravelLocation(
  medplum: MedplumClient,
  patient: Patient,
  answers: PatientAccessCallAnswers
): Promise<void> {
  const market = insuranceTravelMarket(answerString(answers.values[PN_INSURANCE_TRAVEL_ID]));
  if (!market || !patient.id) return;
  const input: SetPatientIntakeSelectionInput = {
    patientId: patient.id,
    url: MARKET_LOCATION_EXTENSION,
    value: market,
  };
  await fetchBot(medplum, 'set-patient-intake-selection', input);
}
